import { Box, Button, HStack, Icon, Stack, useToast } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { useQueryClient } from '@tanstack/react-query';
import { AccountCredentialsCard } from 'components/AccountCredentialsCard';
import { AccountStatusCard } from 'components/AccountStatusCard';
import FormEmbeddedAlertModal from 'components/modals/FormEmbeddedAlertModal';
import { TotpSetupCard } from 'components/TotpSetupCard';
import { useAxios } from 'context/AxiosContextProvider';
import { useUserContext } from 'context/UserContextProvider';
import useCheckTaskMutation from 'hooks/mutations/useCheckTaskMutation';
import useAccountV2Query from 'hooks/useAccountV2Query';
import { get } from 'lodash';
import AccountRecentlyCheckedCard from 'pages/tasks/AccountRecentlyCheckedCard';
import TaskBannerCard from 'pages/tasks/TaskBannerCard';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { IoIosWarning } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';

import { DetailPageTask, Task } from '../../api/types';
import ActivityLogCard from '../../components/ActivityLogCard';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { TasksTableCard } from '../../components/TasksTableCard';
import { Warning } from '../../components/Warning';
import localconfig from '../../config';
import { getStatus } from '../../helpers/general';
import { manageErrorResponse } from '../../helpers/manageErrorResponse';
import useAssignMutation from '../../hooks/mutations/useAssignMutation';
import useAssignNextTaskMutation from '../../hooks/mutations/useAssignNextTaskMutation';
import useAccountDetailQuery from '../../hooks/useAccountDetailQuery';
import { ContentLayout } from '../../layout/ContentLayout';
import PageMetatags from '../../layout/PageMetatags';
import AccountCard from './AccountCard';
import WorkshopCard from './WorkshopCard';

const AccountDetailsOverviewCard = ({ account_id, type }: { account_id: number; type: string | undefined; fastlane: number | undefined }) => {
  const { statusCodes, opUsers, user, isMember } = useUserContext();
  const accountStatusCodes = statusCodes?.account || {};
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const axios = useAxios();
  const queryClient = useQueryClient();

  const accountDetailQuery = useAccountDetailQuery(account_id);
  const queryToInvalidate = () => queryClient.invalidateQueries({ queryKey: useAccountV2Query.getKey(account_id) });
  const accountData = accountDetailQuery.data;
  const workshop = accountDetailQuery.data?.workshop;
  const tasks = (accountData as { tasks?: DetailPageTask[] } | undefined)?.tasks;
  const queryKey = ['AccountDetail'];

  const assignMutation = useAssignMutation(accountData?.open_task?.id as number, useAccountDetailQuery.getKey(account_id));
  const openTaskAssignedToUser = accountData?.open_task?.assignee?.id === user?.id;
  const dateTaskCheckedByUser = accountData?.open_task?.last_checked_at;
  const isSubjectOnHoldOrClarification =
    accountData?.status === getStatus(accountStatusCodes, 'registration_on_hold') ||
    accountData?.status === getStatus(accountStatusCodes, 'deletion_on_hold') ||
    accountData?.status === getStatus(accountStatusCodes, 'deletion_pending') ||
    accountData?.status === getStatus(accountStatusCodes, 'clarification');

  const workshopDetailsCard = React.createElement(AccountCard, {
    workshop: workshop,
    account: accountData,
    isLoading: accountDetailQuery.isFetching || accountDetailQuery.isLoading || !accountDetailQuery.isFetched,
  });

  const checkTaskMutation = useCheckTaskMutation(accountData?.open_task?.id as number, queryKey);

  //@ts-ignore
  const sendRequest = async ({ url, method = 'put', dataForEndpoint = {} }) => {
    const axiosCall =
      method !== 'delete'
        ? // @ts-ignore
          axios[method](url, dataForEndpoint)
        : // @ts-ignore
          axios[method](url);
    return await axiosCall
      .catch((err: { response: { status: number; data?: { message?: string } } }) => {
        manageErrorResponse(toast, err.response);

        // the account got retired instead of registered (a deletion task was created for it) --
        // close the modal and refresh so the page reflects the new status/task instead of
        // showing stale data behind a modal claiming the account isn't registered yet
        if (err.response?.data?.message === "Can't set account to registered since VAG account already exist") {
          queryClient.invalidateQueries({ queryKey: queryKey });
          queryClient.invalidateQueries({ queryKey: ['WorkshopAccountsQuery'] });
          NiceModal.hide(FormEmbeddedAlertModal);
        }

        return Promise.reject(err);
      })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: queryKey });
        queryClient.invalidateQueries({
          queryKey: ['WorkshopAccountsQuery'],
        });
      });
  };
  const assignNextTaskMutation = useAssignNextTaskMutation(type ?? '');

  const enum AccountStatusMapping {
    ON_HOLD = 150,
  }

  const enum AccountStatusReasonMapping {
    WAITING_FOR_ACTIVATION = 151,
  }

  const manualActivationRequired =
    accountData?.open_task?.type === 'account_registration' &&
    accountData?.latest_automation &&
    accountData?.latest_automation?.status !== 700 &&
    accountData?.status === AccountStatusMapping.ON_HOLD &&
    accountData?.status_reason === AccountStatusReasonMapping.WAITING_FOR_ACTIVATION;

  if (accountDetailQuery.isLoading) {
    return <LoadingSpinner />;
  }
  if (accountDetailQuery.isError) {
    navigate('/404', { replace: true });
    return null;
  }

  return (
    <>
      {accountData?.open_task ? (
        <TaskBannerCard
          queryToInvalidate={queryToInvalidate}
          opUsers={opUsers}
          currentUserId={user?.id}
          assignee={accountData?.open_task?.assignee}
          isLoading={accountDetailQuery.isFetching}
          assign={() =>
            assignMutation.mutate({
              updated_at: get(accountData, 'open_task.updated_at', accountData?.updated_at),
            })
          }
          reassign={(user_id) =>
            assignMutation.mutate({
              force: true,
              user_id,
              updated_at: get(accountData, 'open_task.updated_at', accountData?.updated_at),
            })
          }
          task={accountData?.open_task as Task}
        />
      ) : null}
      <ContentLayout
        sidebar={
          <>
            {type ? (
              <Button
                variant="primary"
                disabled={!isMember('tasks:assign-next')}
                data-test-id={`service_history_request_assign_button`}
                onClick={() =>
                  assignNextTaskMutation.mutate(
                    //@ts-ignore
                    {},
                    {
                      onSuccess: (task: Task) => {
                        if (task?.id) {
                          navigate(`/app/op/account/${task.subject_id}/${type}`);
                        } else {
                          navigate(`/app/op/break`);
                        }
                      },
                    },
                  )
                }
              >
                {t('common:next_activity')}
              </Button>
            ) : null}
            <WorkshopCard statusCodes={statusCodes?.workshop} isLoading={accountDetailQuery.isFetching} workshop={workshop} />
          </>
        }
      >
        <>
          <PageMetatags title={t('pages:account.meta.title')} />
          <AccountStatusCard
            isLoading={accountDetailQuery.isPending || accountDetailQuery.isFetching}
            account={accountData}
            openTaskAssignedToUser={openTaskAssignedToUser}
            sendRequest={(formData) =>
              sendRequest({
                url: `${localconfig.apiBaseUrl}/op/v2/account/${account_id}`,
                dataForEndpoint: {
                  ...formData,
                  status_reason:
                    // @ts-ignore
                    formData?.status_reason === 0
                      ? null
                      : // @ts-ignore
                        parseInt(formData?.status_reason),
                },
              })
            }
          />

          {accountData?.open_task && isSubjectOnHoldOrClarification ? (
            <AccountRecentlyCheckedCard
              accountData={accountData}
              dateTaskCheckedByUser={accountData?.open_task?.last_checked_at}
              checkTask={() => {
                checkTaskMutation.mutate();
              }}
            />
          ) : null}
          {!accountData?.open_task && isSubjectOnHoldOrClarification ? (
            <AccountRecentlyCheckedCard
              accountData={accountData}
              dateTaskCheckedByUser={dateTaskCheckedByUser}
              checkTask={() => {
                checkTaskMutation.mutate();
              }}
            />
          ) : null}

          {manualActivationRequired && (
            <Warning bgColor={'purple.100'} data-test-id="manuel-activation-reqiured">
              <Stack textAlign={'center'} alignItems={'center'}>
                <HStack>
                  <Box display={'flex'}>
                    <Icon color={'orange'} as={IoIosWarning} boxSize="16" />
                  </Box>
                  <Box>
                    <Trans i18nKey={`account:status.registration_on_hold.manual_activation_needed`} />
                  </Box>
                </HStack>
              </Stack>
            </Warning>
          )}

          {workshopDetailsCard}

          {isSubjectOnHoldOrClarification ? (
            <AccountRecentlyCheckedCard
              accountData={accountData}
              dateTaskCheckedByUser={dateTaskCheckedByUser}
              checkTask={() => {
                checkTaskMutation.mutate();
              }}
            />
          ) : null}

          <AccountCredentialsCard
            account={accountData}
            sendRequest={(formData) =>
              sendRequest({
                url: `${localconfig.apiBaseUrl}/op/v2/account/${account_id}`,
                dataForEndpoint: formData,
              })
            }
          />

          {!accountData?.has_totp_secret && accountData?.portal?.requires_totp && <TotpSetupCard account_id={account_id} />}
        </>
        <TasksTableCard tasks={tasks} isLoading={accountDetailQuery.isPending} />
        <ActivityLogCard
          workshopName={workshop?.name}
          resourceKey="account"
          queryKey={queryKey}
          activities={accountData?.activities}
          account_id={account_id}
        />
      </ContentLayout>
    </>
  );
};

export default AccountDetailsOverviewCard;
